import { useCallback } from 'react'
import { api, describeError } from '../lib/api'
import { formatBytes } from '../lib/format'
import { useLoad } from '../hooks/useLoad'
import { IconButton } from './IconButton'
import { RefreshIcon } from './Icons'
import { Spinner } from './Spinner'

const FREE_WARN_BYTES = 5 * 1024 ** 3
const FREE_CRITICAL_BYTES = 1 * 1024 ** 3

/**
 * `GET /api/admin/stats` (docs/CONTRACT.md §4) as the header's second line: five numbers, no
 * labels competing with the page's own heading. Disk free is the one that can go bad, so it is the
 * one that changes colour.
 */
export function StatsStrip() {
  const load = useCallback(() => api.adminStats(), [])
  const { data, loading, error, reload } = useLoad(load, (caught) =>
    describeError(caught, 'Could not load server stats.'),
  )

  const critical = data !== null && data.diskFreeBytes < FREE_CRITICAL_BYTES
  const warning = data !== null && !critical && data.diskFreeBytes < FREE_WARN_BYTES

  return (
    <div className="flex min-h-9 flex-wrap items-center gap-x-4 gap-y-1 text-meta text-muted">
      <span className="font-semibold text-ink">Server</span>
      {loading && <Spinner size={14} label="Loading server stats" />}
      {error && (
        <span role="alert" className="text-danger">
          {error}
        </span>
      )}
      {data && (
        <>
          <span>
            <span className="numeric text-ink">{data.users}</span> users
          </span>
          <span>
            <span className="numeric text-ink">{data.chats}</span> chats
          </span>
          <span>
            <span className="numeric text-ink">{data.messages}</span> messages
          </span>
          <span>
            media <span className="numeric text-ink">{formatBytes(data.mediaBytes)}</span>
          </span>
          <span className={critical ? 'text-danger' : warning ? 'text-warn' : undefined}>
            disk free <span className="numeric text-ink">{formatBytes(data.diskFreeBytes)}</span>
          </span>
        </>
      )}
      <IconButton label="Refresh server stats" size={32} onClick={reload} disabled={loading}>
        <RefreshIcon size={16} />
      </IconButton>
    </div>
  )
}
